<script setup lang="ts">
import { ChevronDown, ChevronUp } from 'lucide-vue-next';
import { ref, computed } from 'vue';
import { Button } from '@/components/ui/button';
import { getEventLabel } from '@/composables/useActivityLabels';
import type { RunEvent } from '@/composables/useRunProgress';

const PREVIEW_COUNT = 5;

const props = defineProps<{
    events: RunEvent[];
}>();

const expanded = ref(false);

const displayedEvents = computed(() =>
    expanded.value ? props.events : props.events.slice(-PREVIEW_COUNT),
);

const hasMore = computed(() => props.events.length > PREVIEW_COUNT);

function formatTime(iso: string): string {
    return new Date(iso).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
}
</script>

<template>
    <div v-if="events.length > 0" class="mt-4 border-t border-border pt-4">
        <div class="flex items-center justify-between">
            <p
                class="text-xs font-medium tracking-wide text-muted-foreground uppercase"
            >
                Historique des étapes
            </p>
            <Button
                v-if="hasMore"
                variant="ghost"
                size="sm"
                class="h-6 gap-1 px-2 text-xs text-muted-foreground"
                @click="expanded = !expanded"
            >
                <component
                    :is="expanded ? ChevronUp : ChevronDown"
                    class="h-3 w-3"
                />
                {{ expanded ? 'Réduire' : `Voir tout (${events.length})` }}
            </Button>
        </div>

        <ol class="mt-3 space-y-2">
            <li
                v-for="(event, index) in displayedEvents"
                :key="index"
                class="flex items-start gap-3"
            >
                <!-- Dot -->
                <div
                    class="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center"
                >
                    <div
                        class="h-1.5 w-1.5 rounded-full bg-muted-foreground/50"
                    />
                </div>
                <!-- Content -->
                <div class="min-w-0 flex-1">
                    <p class="text-xs text-foreground">
                        {{ getEventLabel(event.type) }}
                    </p>
                </div>
                <!-- Time -->
                <time class="shrink-0 text-xs text-muted-foreground">
                    {{ formatTime(event.timestamp) }}
                </time>
            </li>
        </ol>
    </div>
</template>
